import axios from 'axios';
import {
  GENERATE_COMIC_DIALOG_URL,
  GENERATE_COMIC_URL,
} from '../../constants/urls';
import { getKeycloakToken } from '../auth/keycloak';
import { ComicStoryboard } from './comicTypes';

export interface GeneratedComicResult {
  pdfUrl: string;
  panelImages: string[];
}

export const generateComicDialogAsync = async (
  formData: FormData,
): Promise<ComicStoryboard> => {
  const token = await getKeycloakToken();

  if (!token) {
    throw new Error('Missing Keycloak token.');
  }

  const response = await axios.post(GENERATE_COMIC_DIALOG_URL, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data as ComicStoryboard;
};

export const generateComicAsync = async function* (data: {
  storyboard: ComicStoryboard;
  oneColor: string;
  colorVariant?: string;
  characterId: string;
  backgroundColor?: string;
  textColor?: string;
  titleFont?: string;
  bodyFont?: string;
  panelImages?: (string | null)[];
  panelImagePrompts?: string[];
}): AsyncGenerator<GeneratedComicResult, void, unknown> {
  const token = await getKeycloakToken();

  if (!token) {
    throw new Error('Missing Keycloak token.');
  }

  const response = await fetch(GENERATE_COMIC_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    window.location.href = '/login';
    return;
  }

  if (!response.ok) {
    throw new Error('Comic generation failed.');
  }

  const result = (await response.json()) as {
    pdfBase64: string;
    panelImages?: string[];
  };
  const pdfBytes = Uint8Array.from(atob(result.pdfBase64), (character) =>
    character.charCodeAt(0),
  );
  const pdfBlob = new Blob([pdfBytes.buffer as ArrayBuffer], {
    type: 'application/pdf',
  });

  yield {
    pdfUrl: URL.createObjectURL(pdfBlob),
    panelImages: result.panelImages ?? [],
  };
};
